import { action, observable, computed, makeObservable } from 'mobx';

import { ILinkPreviewModel } from '@gem/models/commonModel';
import { replaceHtmlTagsToSpecialSymbols } from '@gem/utils';

import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';
import { Quote } from 'STORE/Messages/Message/Quote/Quote';

import Bots from './Bots';
import Mention from './Mention';
import { Voice } from './Voice/Voice';

export interface ISelectionData {
  anchorNodePath: number[];
  focusNodePath: number[];
  anchorOffset: number;
  focusOffset: number;
}

export class InputPanel {
  @observable value: string | null = null;
  @observable editMessageId: string | null = null;
  @observable replyMessage: ChatMessage | null = null;
  @observable quote: Quote | null = null;

  @observable link: string | null = null;
  @observable linkPreviews = observable.map<string, ILinkPreviewModel>({}, { deep: false });
  @observable isLoadingPreview = false;
  @observable linkPreviewCancelled = false;
  @observable isShowLinkPreview = false;
  @observable scrollPosition: number | null = null;
  @observable selectionData: ISelectionData | null = null;

  root: typeof Entities;
  groupId: string;
  mention: Mention;
  bots: Bots;
  voice: Voice;

  constructor(root: typeof Entities, groupId: string) {
    makeObservable(this);
    this.root = root;
    this.groupId = groupId;

    this.mention = new Mention(root, this);
    this.bots = new Bots(root, this);
    this.voice = new Voice(root);
  }

  @action
  setValue = (value: string): void => {
    this.value = value;
  };

  @action
  setEditMessageId = (messageId: string): void => {
    this.editMessageId = messageId;
  };

  @action
  setReplyMessage = (message: ChatMessage): void => {
    this.replyMessage = message;
  };

  @action
  setQuote = (quote: Quote): void => {
    this.quote = quote;
  };

  @computed
  get isEmptyValue(): boolean {
    if (!this.value || !this.value.trim()) {
      return true;
    }

    // remove tags from text
    const div = document.createElement('div');
    div.innerHTML = this.value;
    const text = div.innerText;

    return !text || !text.trim();
  }

  @computed
  get markdownValue(): string {
    return replaceHtmlTagsToSpecialSymbols(this.value);
  }

  @computed
  get group(): Group | null {
    return this.root.GroupsStore.getGroupById(this.groupId);
  }

  @computed
  get editMessage(): ChatMessage | null {
    if (!this.editMessageId) {
      return null;
    }
    return this.group && this.group.messagesStore.getMessageById(this.editMessageId);
  }

  @computed
  get isEditMode(): boolean {
    return Boolean(this.editMessage);
  }

  @action
  setLink = (link: string): void => {
    this.link = link;
  };

  @action
  setIsLoadingPreview = (isLoading: boolean): void => {
    this.isLoadingPreview = isLoading;
  };

  @action
  setIsLinkPreviewCancelled = (value: boolean): void => {
    this.linkPreviewCancelled = value;
  };

  @action
  setIsShowLinkPreview = (value: boolean): void => {
    this.isShowLinkPreview = value;
  };

  @action
  setScrollPosition(value: number): void {
    this.scrollPosition = value;
  }

  @action
  setSelectionData(data: ISelectionData): void {
    this.selectionData = data;
  }
}
